import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { getApiBaseUrl } from '../apiBase';
import Logo from '../components/Logo';

const API = getApiBaseUrl();

const tabStyle = active => ({
    flex: 1,
    padding: 10,
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    fontWeight: 600,
    background: active ? 'var(--sp-primary)' : '#f1f5f9',
    color: active ? '#fff' : '#64748b',
    minHeight: 44,
});

export default function ForgotPasswordPage() {
    const { t } = useTranslation(['auth', 'common']);
    const navigate = useNavigate();
    const [mode, setMode] = useState('email');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [otpCode, setOtpCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [emailSent, setEmailSent] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const [otpChannel, setOtpChannel] = useState(null);
    const [otpTimer, setOtpTimer] = useState(0);
    const [phoneDone, setPhoneDone] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (otpTimer <= 0) return;
        const timerId = setInterval(() => setOtpTimer(s => Math.max(0, s - 1)), 1000);
        return () => clearInterval(timerId);
    }, [otpTimer]);

    async function handleEmailSubmit(e) {
        e.preventDefault();
        setLoading(true);
        try {
            await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
        } catch {
            /* always show success */
        }
        setEmailSent(true);
        setLoading(false);
    }

    async function requestPhoneOtp() {
        setLoading(true);
        setError('');
        try {
            const res = await axios.post(`${API}/auth/forgot-password-phone`, { phone: phone.trim() });
            const channel = res.data?.channel || 'sms';
            setOtpChannel(channel);
            setOtpSent(true);
            setOtpTimer(channel === 'call'
                ? (res.data?.fallbackSmsAvailableAfterSeconds ?? 45)
                : 60);
        } catch (err) {
            setError(err.response?.data?.message || t('errors.otpSendFailed'));
        }
        setLoading(false);
    }

    async function requestSmsFallback() {
        setLoading(true);
        setError('');
        try {
            await axios.post(`${API}/auth/forgot-password-phone-sms-fallback`, { phone: phone.trim() });
            setOtpChannel('sms');
            setOtpTimer(60);
        } catch (err) {
            setError(err.response?.data?.message || t('errors.otpSendFailed'));
        }
        setLoading(false);
    }

    async function handlePhoneReset(e) {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            setError(t('passwordMismatch'));
            return;
        }
        setLoading(true);
        setError('');
        try {
            await axios.post(`${API}/auth/reset-password-phone`, {
                phone: phone.trim(),
                otpCode: otpCode.trim(),
                newPassword,
                confirmPassword,
            });
            setPhoneDone(true);
            setTimeout(() => navigate('/login', { state: { message: t('passwordChanged') } }), 2000);
        } catch (err) {
            setError(err.response?.data?.message || t('errors.generic'));
        }
        setLoading(false);
    }

    return (
        <div className="sp-auth-page page-login">
            <div className="sp-auth-form-panel" style={{ width: '100%', justifyContent: 'center' }}>
                <div style={{ width: '100%', maxWidth: 420, margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: 24 }}>
                        <Logo variant="light" height={40} style={{ margin: '0 auto 24px' }} />
                    </div>
                    <div className="sp-data-card" style={{ padding: 36 }}>
                        <h1 style={{ margin: '0 0 16px', fontSize: 22, textAlign: 'center' }}>
                            {t('forgotMobile.title')}
                        </h1>

                        <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
                            <button type="button" style={tabStyle(mode === 'email')} onClick={() => setMode('email')}>
                                {t('forgotByEmail')}
                            </button>
                            <button type="button" style={tabStyle(mode === 'phone')} onClick={() => setMode('phone')}>
                                {t('forgotByPhone')}
                            </button>
                        </div>

                        {mode === 'email' ? (
                            !emailSent ? (
                                <>
                                    <p style={{ color: '#64748b', fontSize: 14, textAlign: 'center', marginBottom: 24 }}>
                                        {t('forgotMobile.subtitle')}
                                    </p>
                                    <form onSubmit={handleEmailSubmit}>
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                                            {t('common:email')}
                                        </label>
                                        <input
                                            className="sp-input"
                                            type="email"
                                            value={email}
                                            onChange={e => setEmail(e.target.value)}
                                            required
                                            autoComplete="email"
                                            style={{ marginBottom: 20 }}
                                        />
                                        <button
                                            type="submit"
                                            disabled={loading || !email.trim()}
                                            className="sp-btn sp-btn--primary"
                                            style={{ width: '100%' }}
                                        >
                                            {loading ? t('forgotMobile.sending') : t('forgotMobile.submit')}
                                        </button>
                                    </form>
                                </>
                            ) : (
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: 48, marginBottom: 16 }}>✉️</div>
                                    <p style={{ fontSize: 15, lineHeight: 1.6, marginBottom: 20 }}>
                                        {t('forgotMobile.sentSubtitle', { email: email.trim() })}
                                        {' '}
                                        {t('forgotMobile.spamHint')}
                                    </p>
                                    <Link to="/login" className="sp-btn sp-btn--secondary" style={{ display: 'inline-block', textDecoration: 'none' }}>
                                        {t('forgotMobile.backToLogin')}
                                    </Link>
                                </div>
                            )
                        ) : phoneDone ? (
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
                                <p style={{ fontSize: 15, lineHeight: 1.6 }}>{t('passwordChanged')}</p>
                            </div>
                        ) : (
                            <>
                                <p style={{ color: '#64748b', fontSize: 14, textAlign: 'center', marginBottom: 20 }}>
                                    {t('enterPhoneForReset')}
                                </p>
                                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                                    {t('phone')}
                                </label>
                                <input
                                    className="sp-input"
                                    type="tel"
                                    value={phone}
                                    onChange={e => setPhone(e.target.value)}
                                    placeholder="+7 (___) ___-__-__"
                                    autoComplete="tel"
                                    style={{ marginBottom: 16 }}
                                />
                                {!otpSent ? (
                                    <button
                                        type="button"
                                        disabled={loading || !phone.trim()}
                                        onClick={requestPhoneOtp}
                                        className="sp-btn sp-btn--primary"
                                        style={{ width: '100%', marginBottom: 16 }}
                                    >
                                        {loading ? t('forgotMobile.sending') : t('getCode')}
                                    </button>
                                ) : (
                                    <form onSubmit={handlePhoneReset}>
                                        {otpChannel === 'call' ? (
                                            <div style={{
                                                padding: '12px 14px', marginBottom: 16, borderRadius: 10,
                                                background: '#eff6ff', border: '1px solid #bfdbfe', fontSize: 12, color: '#475569',
                                            }}>
                                                <strong style={{ display: 'block', color: '#1e3a5f', marginBottom: 4 }}>
                                                    {t('callOtpTitle')}
                                                </strong>
                                                {t('callOtpDescription')}
                                            </div>
                                        ) : (
                                            <p style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
                                                {t('smsOtpSentHint')}
                                            </p>
                                        )}
                                        <p style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
                                            {t('resetCodeSent')}
                                        </p>
                                        <input
                                            className="sp-input"
                                            value={otpCode}
                                            onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                            maxLength={4}
                                            placeholder={t('callOtpCodePlaceholder')}
                                            inputMode="numeric"
                                            style={{ marginBottom: 16, letterSpacing: 8, textAlign: 'center' }}
                                            required
                                        />
                                        {otpTimer > 0 ? (
                                            <p style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
                                                {t('resendIn', { sec: String(otpTimer).padStart(2, '0') })}
                                            </p>
                                        ) : null}
                                        {otpTimer === 0 && otpChannel === 'call' ? (
                                            <button
                                                type="button"
                                                onClick={requestSmsFallback}
                                                disabled={loading}
                                                style={{
                                                    marginBottom: 16, background: 'none', border: 'none',
                                                    color: '#EB3333', cursor: 'pointer', fontWeight: 600, fontSize: 13,
                                                }}
                                            >
                                                {t('requestSmsFallback')}
                                            </button>
                                        ) : null}
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                                            {t('newPassword')}
                                        </label>
                                        <input
                                            className="sp-input"
                                            type="password"
                                            value={newPassword}
                                            onChange={e => setNewPassword(e.target.value)}
                                            autoComplete="new-password"
                                            style={{ marginBottom: 12 }}
                                            required
                                        />
                                        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                                            {t('confirmNewPassword')}
                                        </label>
                                        <input
                                            className="sp-input"
                                            type="password"
                                            value={confirmPassword}
                                            onChange={e => setConfirmPassword(e.target.value)}
                                            autoComplete="new-password"
                                            style={{ marginBottom: 20 }}
                                            required
                                        />
                                        <button
                                            type="submit"
                                            disabled={loading || otpCode.length < 4}
                                            className="sp-btn sp-btn--primary"
                                            style={{ width: '100%' }}
                                        >
                                            {loading ? t('forgotMobile.sending') : t('changePasswordBtn')}
                                        </button>
                                    </form>
                                )}
                            </>
                        )}

                        {error ? (
                            <div style={{
                                background: '#fce4ec', color: '#c62828', padding: '10px 14px',
                                borderRadius: 8, fontSize: 13, marginTop: 16,
                            }}>
                                {error}
                            </div>
                        ) : null}

                        {!emailSent && !phoneDone ? (
                            <div style={{ textAlign: 'center', marginTop: 20 }}>
                                <Link to="/login" style={{ color: '#EB3333', fontWeight: 600, fontSize: 14 }}>
                                    {t('forgotMobile.backToLogin')}
                                </Link>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
}
